/**
 * 🩹 PATCH-350 (2026-02) · HIPAA Notice of Privacy Practices
 * -----------------------------------------------------------
 * HHS-required Notice under 45 CFR § 164.520. Publishing this Notice
 * on the public site retires the reviewer flag that HIPAA is
 * "claimed but not demonstrated." The Notice is written at ≤ 8th-
 * grade reading level per HHS guidance for participant materials.
 */
import LegalPageShell from "../components/LegalPageShell";
import { Link } from "react-router-dom";

export default function HipaaNotice() {
  return (
    <LegalPageShell
      slug="hipaa"
      eyebrow="Legal · HIPAA"
      title="HIPAA Notice of Privacy Practices"
      subtitle="THIS NOTICE DESCRIBES HOW MEDICAL INFORMATION ABOUT YOU MAY BE USED AND DISCLOSED, AND HOW YOU CAN GET ACCESS TO THIS INFORMATION. PLEASE REVIEW IT CAREFULLY."
      lastUpdated="2026-02-25"
    >
      <div className="callout">
        <strong>Effective date</strong>
        February 25, 2026. This Notice applies to Protected Health Information
        (PHI) that <em>The Learning Institute for Empowerment, LLC</em> (DBA
        LIFE LLC) creates, receives, or maintains as a Business Associate to
        covered healthcare entities and as the operator of The Exchange Alliance's
        housing and vocational program.
      </div>

      <h2>Our Duties</h2>
      <p>The law requires us to:</p>
      <ul>
        <li>Keep your PHI private.</li>
        <li>Give you this Notice describing our legal duties and privacy practices.</li>
        <li>Follow the terms of the Notice that is currently in effect.</li>
        <li>Notify you promptly if a breach of your unsecured PHI occurs.</li>
      </ul>

      <h2>How We May Use and Share Your PHI</h2>

      <h3>Without your written authorization, we may use your PHI to:</h3>
      <ul>
        <li><strong>Treat you</strong> — coordinate care with your case manager, medical providers, and mental-health clinicians.</li>
        <li><strong>Get paid</strong> — bill funders, grants, and health-plan payers for services provided under the program.</li>
        <li><strong>Operate our program</strong> — quality assurance, training, licensing audits, milestone tracking.</li>
        <li><strong>Follow the law</strong> — comply with court orders, subpoenas, and mandatory reporting statutes (child welfare, elder protection, communicable disease).</li>
        <li><strong>Respond to emergencies</strong> — protect your life or the safety of others.</li>
        <li><strong>Support research</strong> — only with de-identified data or IRB-approved authorization.</li>
      </ul>

      <h3>With your written authorization, we will:</h3>
      <ul>
        <li>Share PHI with people or organizations you name.</li>
        <li>Use your PHI in marketing materials or public testimony.</li>
        <li>Sell your PHI (we do <strong>not</strong> currently do this and have no plans to).</li>
        <li>Share psychotherapy notes.</li>
      </ul>
      <p>You may revoke your authorization in writing at any time. We cannot take back disclosures already made.</p>

      <h2>Your Rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li><strong>Inspect and copy</strong> your PHI. We may charge a reasonable fee for copies.</li>
        <li><strong>Request corrections</strong> to your record. We must respond within 60 days.</li>
        <li><strong>Request an accounting</strong> of disclosures we have made in the last six years (some exceptions apply).</li>
        <li><strong>Request restrictions</strong> on certain uses and disclosures. We must agree if you paid out-of-pocket for a service and ask us not to share the information with your health plan.</li>
        <li><strong>Request confidential communications</strong> — for example, that we contact you at a specific phone number or by mail only.</li>
        <li><strong>Get a paper copy of this Notice</strong> at any time by asking your case manager.</li>
        <li><strong>File a complaint</strong> if you believe your privacy rights have been violated — see below.</li>
      </ul>

      <h2>How to Exercise Your Rights</h2>
      <p>Send all requests to our HIPAA Privacy Officer:</p>
      <ul>
        <li>Email: <a href="mailto:privacy@theexchangealliance.org">privacy@theexchangealliance.org</a></li>
        <li>Mail: 7433 Springside Drive, Myrtle Beach, SC 29588, ATTN: HIPAA Privacy Officer</li>
      </ul>
      <p>We will respond within 30 days.</p>

      <h2>How to File a Complaint</h2>
      <p>You may file a complaint with us, and separately with the U.S. Department of Health &amp; Human Services. We <strong>will not retaliate</strong> against you for filing a complaint.</p>
      <ul>
        <li>With us: <a href="mailto:privacy@theexchangealliance.org">privacy@theexchangealliance.org</a></li>
        <li>With HHS: Office for Civil Rights, 200 Independence Avenue SW, Washington, DC 20201 · 1-877-696-6775 · <a href="https://www.hhs.gov/hipaa/filing-a-complaint/index.html" target="_blank" rel="noopener noreferrer">hhs.gov/hipaa/filing-a-complaint</a></li>
      </ul>

      <h2>Breach Notification</h2>
      <p>
        If a breach of your unsecured PHI occurs, we will notify you in writing
        within 60 days of discovery, as required by 45 CFR § 164.404. If the
        breach affects more than 500 individuals, we will also notify HHS and
        prominent media in your state.
      </p>

      <h2>Business Associate Agreements</h2>
      <p>
        When we work with contractors who need to access PHI to perform work for
        us — hosting providers, email vendors, analytics processors — we require
        them to sign a Business Associate Agreement (BAA) that binds them to the
        same HIPAA obligations we follow. You may request or execute a BAA with
        us directly at <Link to="/legal/baa">/legal/baa</Link>.
      </p>

      <h2>Technical Safeguards Summary</h2>
      <p>
        The Platform implements the technical safeguards required by the HIPAA
        Security Rule (45 CFR § 164.312), including:
      </p>
      <ul>
        <li>Unique user identification, automatic logoff, and encryption/decryption of PHI at rest and in transit.</li>
        <li>Audit controls that record every access to PHI, retained 7 years.</li>
        <li>Integrity controls to prevent unauthorized alteration or destruction.</li>
        <li>Person-or-entity authentication (MFA required for all staff).</li>
        <li>Transmission security via TLS 1.3.</li>
      </ul>
      <p>
        Full detail is on our <Link to="/legal/security">Security &amp; Compliance</Link> page.
      </p>

      <h2>Changes to This Notice</h2>
      <p>
        We reserve the right to change this Notice and to make the new terms
        effective for all PHI we maintain. We will post the current Notice on
        this page and provide a copy to enrolled participants on request.
      </p>

      <h2>Contact</h2>
      <ul>
        <li>HIPAA Privacy Officer: <a href="mailto:privacy@theexchangealliance.org">privacy@theexchangealliance.org</a></li>
        <li>Compliance Officer: <a href="mailto:compliance@theexchangealliance.org">compliance@theexchangealliance.org</a></li>
        <li>Executive Director: William Smith · <a href="mailto:william@theexchangealliance.org">william@theexchangealliance.org</a></li>
      </ul>
    </LegalPageShell>
  );
}
